import React, { useEffect, useState, Component } from 'react';
import { Plus, Edit2, Trash2, User, Shield, X, Eye, EyeOff, RefreshCw, AlertCircle, ChevronDown, ChevronRight, LayoutDashboard, Package, Tag, MessageSquare, Building2, Users, UtensilsCrossed, Percent, BarChart3, FileText, Settings, MessageCircle, CheckSquare, Square, Zap, LogIn, Truck, Briefcase } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../contexts/AuthContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getCompanies } from '../utils/companyManagement';
// ─── Permission Definition System ───────────────────────────────────────────
interface PermissionDef {
  key: string;
  label: string;
  description: string;
}
interface PermissionGroup {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  permissions: PermissionDef[];
}
const PERMISSION_GROUPS: PermissionGroup[] = [{
  id: 'orders',
  label: 'Órdenes',
  icon: LayoutDashboard,
  color: 'blue',
  permissions: [{
    key: 'orders.view',
    label: 'Ver órdenes',
    description: 'Acceder al listado y detalle de pedidos'
  }, {
    key: 'orders.create',
    label: 'Crear órdenes',
    description: 'Crear nuevos pedidos manualmente'
  }, {
    key: 'orders.edit',
    label: 'Editar estado',
    description: 'Cambiar estado de pedidos (preparar, entregar)'
  }, {
    key: 'orders.delete',
    label: 'Cancelar órdenes',
    description: 'Cancelar o eliminar pedidos existentes'
  }, {
    key: 'orders.export',
    label: 'Exportar',
    description: 'Descargar listados de órdenes'
  }]
}, {
  id: 'delivery',
  label: 'Mis Entregas',
  icon: Truck,
  color: 'blue',
  permissions: [{
    key: 'delivery.view',
    label: 'Ver mis entregas',
    description: 'Acceder a la vista de entregas asignadas'
  }]
}, {
  id: 'assignments',
  label: 'Asignaciones',
  icon: Briefcase,
  color: 'amber',
  permissions: [{
    key: 'assignments.view',
    label: 'Ver asignaciones',
    description: 'Acceder al panel de asignaciones de pedidos'
  }, {
    key: 'assignments.manage',
    label: 'Gestionar asignaciones',
    description: 'Asignar y reasignar pedidos a repartidores'
  }]
}, {
  id: 'production',
  label: 'Producción',
  icon: Package,
  color: 'amber',
  permissions: [{
    key: 'production.view',
    label: 'Ver producción',
    description: 'Acceder a la vista de producción'
  }]
}, {
  id: 'labels',
  label: 'Etiquetas',
  icon: Tag,
  color: 'teal',
  permissions: [{
    key: 'labels.view',
    label: 'Ver etiquetas',
    description: 'Acceder al control de etiquetas'
  }, {
    key: 'labels.print',
    label: 'Imprimir',
    description: 'Imprimir etiquetas de pedidos'
  }, {
    key: 'labels.manage',
    label: 'Gestionar estados',
    description: 'Marcar etiquetas como generadas'
  }]
}, {
  id: 'claims',
  label: 'Reclamaciones',
  icon: MessageSquare,
  color: 'rose',
  permissions: [{
    key: 'claims.view',
    label: 'Ver reclamaciones',
    description: 'Acceder al listado de reclamaciones'
  }, {
    key: 'claims.create',
    label: 'Crear',
    description: 'Registrar nuevas reclamaciones'
  }, {
    key: 'claims.resolve',
    label: 'Resolver',
    description: 'Resolver y cerrar reclamaciones'
  }]
}, {
  id: 'companies',
  label: 'Empresas',
  icon: Building2,
  color: 'violet',
  permissions: [{
    key: 'companies.view',
    label: 'Ver empresas',
    description: 'Acceder al listado de empresas'
  }, {
    key: 'companies.create',
    label: 'Crear',
    description: 'Registrar nuevas empresas'
  }, {
    key: 'companies.edit',
    label: 'Editar',
    description: 'Modificar datos de empresas'
  }, {
    key: 'companies.delete',
    label: 'Eliminar',
    description: 'Eliminar empresas del sistema'
  }, {
    key: 'companies.managePrices',
    label: 'Precios especiales',
    description: 'Gestionar precios por empresa'
  }]
}, {
  id: 'employees',
  label: 'Empleados',
  icon: Users,
  color: 'cyan',
  permissions: [{
    key: 'employees.view',
    label: 'Ver empleados',
    description: 'Acceder al listado de empleados'
  }, {
    key: 'employees.create',
    label: 'Crear',
    description: 'Registrar nuevos empleados'
  }, {
    key: 'employees.edit',
    label: 'Editar',
    description: 'Modificar datos de empleados'
  }, {
    key: 'employees.delete',
    label: 'Eliminar',
    description: 'Eliminar empleados del sistema'
  }, {
    key: 'employees.resetPassword',
    label: 'Restablecer contraseña',
    description: 'Cambiar contraseñas de empleados'
  }]
}, {
  id: 'menu',
  label: 'Menú',
  icon: UtensilsCrossed,
  color: 'orange',
  permissions: [{
    key: 'menu.view',
    label: 'Ver menú',
    description: 'Acceder al catálogo de platos'
  }, {
    key: 'menu.create',
    label: 'Crear platos',
    description: 'Agregar nuevos platos al menú'
  }, {
    key: 'menu.edit',
    label: 'Editar platos',
    description: 'Modificar platos existentes'
  }, {
    key: 'menu.delete',
    label: 'Eliminar platos',
    description: 'Eliminar platos del menú'
  }, {
    key: 'menu.manageCategories',
    label: 'Categorías',
    description: 'Crear y eliminar categorías'
  }, {
    key: 'menu.manageSections',
    label: 'Secciones y opciones',
    description: 'Gestionar opciones de personalización'
  }]
}, {
  id: 'promotions',
  label: 'Promociones',
  icon: Percent,
  color: 'pink',
  permissions: [{
    key: 'promotions.view',
    label: 'Ver promociones',
    description: 'Acceder al listado de promociones'
  }, {
    key: 'promotions.create',
    label: 'Crear',
    description: 'Crear nuevas promociones'
  }, {
    key: 'promotions.edit',
    label: 'Editar',
    description: 'Modificar promociones existentes'
  }, {
    key: 'promotions.delete',
    label: 'Eliminar',
    description: 'Eliminar promociones'
  }, {
    key: 'promotions.toggle',
    label: 'Activar/Desactivar',
    description: 'Cambiar estado de promociones'
  }]
}, {
  id: 'reports',
  label: 'Reportes',
  icon: BarChart3,
  color: 'emerald',
  permissions: [{
    key: 'reports.view',
    label: 'Ver reportes',
    description: 'Acceder a reportes y estadísticas'
  }, {
    key: 'reports.export',
    label: 'Exportar',
    description: 'Descargar reportes en Excel/PDF'
  }]
}, {
  id: 'activityLog',
  label: 'Registro de Actividad',
  icon: FileText,
  color: 'slate',
  permissions: [{
    key: 'activityLog.view',
    label: 'Ver registro',
    description: 'Acceder al historial de actividad'
  }]
}, {
  id: 'settings',
  label: 'Configuración',
  icon: Settings,
  color: 'gray',
  permissions: [{
    key: 'settings.view',
    label: 'Ver configuración',
    description: 'Acceder a la configuración del sistema'
  }, {
    key: 'settings.edit',
    label: 'Editar general',
    description: 'Modificar configuración general'
  }, {
    key: 'settings.editTerms',
    label: 'Editar términos',
    description: 'Modificar términos y condiciones'
  }, {
    key: 'settings.editDeliveryNote',
    label: 'Editar conduce',
    description: 'Modificar formato de conduce'
  }]
}, {
  id: 'users',
  label: 'Usuarios Admin',
  icon: Shield,
  color: 'red',
  permissions: [{
    key: 'users.view',
    label: 'Ver usuarios',
    description: 'Acceder al listado de administradores'
  }, {
    key: 'users.create',
    label: 'Crear',
    description: 'Crear nuevos usuarios administrativos'
  }, {
    key: 'users.edit',
    label: 'Editar',
    description: 'Modificar datos de usuarios'
  }, {
    key: 'users.delete',
    label: 'Eliminar',
    description: 'Eliminar usuarios administrativos'
  }, {
    key: 'users.managePermissions',
    label: 'Gestionar permisos',
    description: 'Cambiar permisos de otros usuarios'
  }]
}, {
  id: 'chat',
  label: 'Chat de Soporte',
  icon: MessageCircle,
  color: 'green',
  permissions: [{
    key: 'chat.view',
    label: 'Ver conversaciones',
    description: 'Acceder al chat de soporte'
  }, {
    key: 'chat.respond',
    label: 'Responder',
    description: 'Enviar mensajes a empleados'
  }]
}];
// All permission keys
const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));
// Role presets
type PresetId = 'full' | 'supervisor' | 'readonly' | 'custom';
interface RolePreset {
  id: PresetId;
  label: string;
  description: string;
  icon: React.ElementType;
  getPermissions: () => Record<string, boolean>;
}
const ROLE_PRESETS: RolePreset[] = [{
  id: 'full',
  label: 'Acceso Total',
  description: 'Todos los permisos habilitados',
  icon: Shield,
  getPermissions: () => {
    const perms: Record<string, boolean> = {};
    ALL_PERMISSION_KEYS.forEach(k => perms[k] = true);
    return perms;
  }
}, {
  id: 'supervisor',
  label: 'Supervisor',
  description: 'Operaciones diarias sin configuración',
  icon: Eye,
  getPermissions: () => {
    const perms: Record<string, boolean> = {};
    ALL_PERMISSION_KEYS.forEach(k => perms[k] = false);
    ['orders.view', 'orders.create', 'orders.edit', 'orders.export', 'production.view', 'labels.view', 'labels.print', 'labels.manage', 'claims.view', 'claims.create', 'claims.resolve', 'companies.view', 'employees.view', 'menu.view', 'promotions.view', 'reports.view', 'activityLog.view', 'chat.view', 'chat.respond'].forEach(k => perms[k] = true);
    return perms;
  }
}, {
  id: 'readonly',
  label: 'Solo Lectura',
  description: 'Solo puede ver información, sin modificar',
  icon: Eye,
  getPermissions: () => {
    const perms: Record<string, boolean> = {};
    ALL_PERMISSION_KEYS.forEach(k => perms[k] = false);
    // Read-only: only .view permissions
    ALL_PERMISSION_KEYS.filter(k => k.endsWith('.view')).forEach(k => perms[k] = true);
    return perms;
  }
}, {
  id: 'custom',
  label: 'Personalizado',
  description: 'Configura cada permiso individualmente',
  icon: Settings,
  getPermissions: () => {
    const perms: Record<string, boolean> = {};
    ALL_PERMISSION_KEYS.forEach(k => perms[k] = false);
    return perms;
  }
}];
// Convert granular permissions to legacy format for DB compatibility
function toDbPermissions(granular: Record<string, boolean>): Record<string, any> {
  return {
    // Legacy keys for backward compatibility
    canManageOrders: granular['orders.view'] || false,
    canManageMenu: granular['menu.view'] || false,
    canManageEmployees: granular['employees.view'] || false,
    canManageCompanies: granular['companies.view'] || false,
    canViewReports: granular['reports.view'] || false,
    canManageUsers: granular['users.view'] || false,
    // Store full granular permissions
    granular
  };
}
// Convert DB permissions (legacy or granular) to granular format
function fromDbPermissions(dbPerms: any): Record<string, boolean> {
  // If granular permissions exist, use them
  if (dbPerms?.granular && typeof dbPerms.granular === 'object') {
    const result: Record<string, boolean> = {};
    ALL_PERMISSION_KEYS.forEach(k => {
      result[k] = dbPerms.granular[k] === true;
    });
    return result;
  }
  // Fallback: map legacy permissions to granular
  const perms: Record<string, boolean> = {};
  ALL_PERMISSION_KEYS.forEach(k => perms[k] = false);
  if (dbPerms?.canManageOrders) {
    ;
    ['orders.view', 'orders.create', 'orders.edit', 'orders.delete', 'orders.export', 'production.view', 'labels.view', 'labels.print', 'labels.manage', 'claims.view', 'claims.create', 'claims.resolve'].forEach(k => perms[k] = true);
  }
  if (dbPerms?.canManageMenu) {
    ;
    ['menu.view', 'menu.create', 'menu.edit', 'menu.delete', 'menu.manageCategories', 'menu.manageSections', 'promotions.view', 'promotions.create', 'promotions.edit', 'promotions.delete', 'promotions.toggle'].forEach(k => perms[k] = true);
  }
  if (dbPerms?.canManageEmployees) {
    ;
    ['employees.view', 'employees.create', 'employees.edit', 'employees.delete', 'employees.resetPassword'].forEach(k => perms[k] = true);
  }
  if (dbPerms?.canManageCompanies) {
    ;
    ['companies.view', 'companies.create', 'companies.edit', 'companies.delete', 'companies.managePrices'].forEach(k => perms[k] = true);
  }
  if (dbPerms?.canViewReports) {
    ;
    ['reports.view', 'reports.export', 'activityLog.view'].forEach(k => perms[k] = true);
  }
  if (dbPerms?.canManageUsers) {
    ;
    ['users.view', 'users.create', 'users.edit', 'users.delete', 'users.managePermissions', 'settings.view', 'settings.edit', 'settings.editTerms', 'settings.editDeliveryNote'].forEach(k => perms[k] = true);
  }
  // Chat always on for legacy
  perms['chat.view'] = true;
  perms['chat.respond'] = true;
  return perms;
}
// Detect which preset matches
function detectPreset(perms: Record<string, boolean>): PresetId {
  const allTrue = ALL_PERMISSION_KEYS.every(k => perms[k] === true);
  if (allTrue) return 'full';
  const supervisorPerms = ROLE_PRESETS.find(p => p.id === 'supervisor')!.getPermissions();
  const matchesSupervisor = ALL_PERMISSION_KEYS.every(k => perms[k] === supervisorPerms[k]);
  if (matchesSupervisor) return 'supervisor';
  const readonlyPerms = ROLE_PRESETS.find(p => p.id === 'readonly')!.getPermissions();
  const matchesReadonly = ALL_PERMISSION_KEYS.every(k => perms[k] === readonlyPerms[k]);
  if (matchesReadonly) return 'readonly';
  return 'custom';
}
// ─── Color utilities ────────────────────────────────────────────────────────
const colorMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  bgLight: string;
}> = {
  blue: {
    bg: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20'
  },
  amber: {
    bg: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20'
  },
  teal: {
    bg: 'bg-teal-500',
    text: 'text-teal-600 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-800',
    bgLight: 'bg-teal-50 dark:bg-teal-900/20'
  },
  rose: {
    bg: 'bg-rose-500',
    text: 'text-rose-600 dark:text-rose-400',
    border: 'border-rose-200 dark:border-rose-800',
    bgLight: 'bg-rose-50 dark:bg-rose-900/20'
  },
  violet: {
    bg: 'bg-violet-500',
    text: 'text-violet-600 dark:text-violet-400',
    border: 'border-violet-200 dark:border-violet-800',
    bgLight: 'bg-violet-50 dark:bg-violet-900/20'
  },
  cyan: {
    bg: 'bg-cyan-500',
    text: 'text-cyan-600 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-800',
    bgLight: 'bg-cyan-50 dark:bg-cyan-900/20'
  },
  orange: {
    bg: 'bg-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    bgLight: 'bg-orange-50 dark:bg-orange-900/20'
  },
  pink: {
    bg: 'bg-pink-500',
    text: 'text-pink-600 dark:text-pink-400',
    border: 'border-pink-200 dark:border-pink-800',
    bgLight: 'bg-pink-50 dark:bg-pink-900/20'
  },
  emerald: {
    bg: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20'
  },
  slate: {
    bg: 'bg-slate-500',
    text: 'text-slate-600 dark:text-slate-400',
    border: 'border-slate-200 dark:border-slate-800',
    bgLight: 'bg-slate-50 dark:bg-slate-900/20'
  },
  gray: {
    bg: 'bg-gray-500',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-200 dark:border-gray-800',
    bgLight: 'bg-gray-50 dark:bg-gray-900/20'
  },
  red: {
    bg: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
    bgLight: 'bg-red-50 dark:bg-red-900/20'
  },
  green: {
    bg: 'bg-green-500',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    bgLight: 'bg-green-50 dark:bg-green-900/20'
  }
};
// ─── AdminUser Interface ────────────────────────────────────────────────────
interface AdminUser {
  id: string;
  username: string;
  name: string;
  email: string;
  role: string;
  permissions: any;
  status: string;
  created_at: string;
}
// ─── Main Component ─────────────────────────────────────────────────────────
export function UserManagement() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    showToast
  } = useToast();
  const {
    user: currentUser,
    isImpersonating,
    impersonateUser
  } = useAuth();
  // Show impersonate button for any full-access admin (no granular restrictions)
  // This covers the main admin regardless of their username
  const canImpersonate = !isImpersonating && currentUser?.role === 'admin' && !currentUser?.granularPermissions; // no granular = full access admin
  useEffect(() => {
    loadUsers();
  }, []);
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!isSupabaseConfigured()) {
        setError('Supabase no está configurado');
        return;
      }
      const data = await supabaseClient.getAdminUsers();
      const normalized = (data || []).map((u: any) => ({
        ...u,
        permissions: u.permissions || {}
      }));
      setUsers(normalized);
    } catch (err: any) {
      if (err.message?.includes('list_admin_users') || err.message?.includes('does not exist')) {
        setError('Ejecuta SUPABASE_ADMIN_MANAGEMENT.sql en Supabase para habilitar la gestión de admins desde la web.');
      } else {
        setError(err.message || 'Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };
  const handleAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };
  const handleEdit = (user: AdminUser) => {
    setEditingUser(user);
    setShowModal(true);
  };
  const handleDelete = (user: AdminUser) => {
    if (user.username === 'admin') {
      showToast('error', 'No permitido', 'No puedes eliminar el administrador principal');
      return;
    }
    setDeleteConfirm(user);
  };
  const handleImpersonate = (targetUser: AdminUser) => {
    if (targetUser.username === 'admin') return;
    impersonateUser(targetUser);
    showToast('success', 'Sesión iniciada', `Ahora estás viendo el panel como ${targetUser.name}`);
  };
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await supabaseClient.deleteAdminUser(deleteConfirm.id);
      showToast('success', 'Usuario eliminado', `${deleteConfirm.name} ha sido eliminado`);
      setDeleteConfirm(null);
      await loadUsers();
    } catch (err: any) {
      showToast('error', 'Error', err.message || 'No se pudo eliminar el usuario');
    }
  };
  const handleSave = async (userData: any) => {
    try {
      if (editingUser) {
        await supabaseClient.updateAdminUser(editingUser.id, {
          name: userData.name,
          email: userData.email,
          password: userData.password || undefined,
          role: userData.role,
          status: userData.status,
          permissions: userData.permissions
        });
        showToast('success', 'Usuario actualizado', `${userData.name} ha sido actualizado`);
      } else {
        if (!userData.password) {
          showToast('error', 'Error', 'La contraseña es requerida para nuevos usuarios');
          return;
        }
        await supabaseClient.createAdminUser({
          username: userData.username,
          password: userData.password,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          permissions: userData.permissions
        });
        showToast('success', 'Usuario creado', `${userData.name} ha sido creado de forma segura`);
      }
      setShowModal(false);
      setEditingUser(null);
      await loadUsers();
    } catch (err: any) {
      showToast('error', 'Error', err.message || 'No se pudo guardar el usuario');
    }
  };
  const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.username.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()));
  const getPermissionSummary = (perms: any) => {
    const granular = fromDbPermissions(perms);
    const enabled = ALL_PERMISSION_KEYS.filter(k => granular[k] === true).length;
    return {
      enabled,
      total: ALL_PERMISSION_KEYS.length
    };
  };
  if (loading) {
    return <div className="flex items-center justify-center py-20" data-id="element-6228">
        <div className="text-center" data-id="element-6229">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" data-id="element-6230" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-6231">
            Cargando usuarios...
          </p>
        </div>
      </div>;
  }
  if (error) {
    return <div className="flex items-center justify-center py-20" data-id="element-6232">
        <Card className="max-w-lg w-full" data-id="element-6233">
          <div className="text-center p-6" data-id="element-6234">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6235">
              <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" data-id="element-6236" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-6237">
              Configuración requerida
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm" data-id="element-6238">
              {error}
            </p>
            <Button onClick={loadUsers} leftIcon={<RefreshCw size={16} data-id="element-6240" />} variant="outline" data-id="element-6239">
              Reintentar
            </Button>
          </div>
        </Card>
      </div>;
  }
  return <div data-id="element-6241">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-6242">
        <div data-id="element-6243">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-6244">
            Usuarios Administrativos
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-6245">
            Gestiona los usuarios con acceso al panel de administración
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1" data-id="element-6246">
            <Shield size={12} data-id="element-6247" /> Contraseñas protegidas con bcrypt server-side
          </p>
        </div>
        <div className="flex gap-2" data-id="element-6248">
          <Button variant="outline" leftIcon={<RefreshCw size={16} data-id="element-6250" />} onClick={loadUsers} data-id="element-6249">
            Actualizar
          </Button>
          <Button leftIcon={<Plus size={18} data-id="element-6252" />} onClick={handleAddUser} data-id="element-6251">
            Nuevo Usuario
          </Button>
        </div>
      </div>

      <div className="mb-6" data-id="element-6253">
        <SearchBar placeholder="Buscar usuario..." onSearch={setSearchTerm} className="w-full sm:w-96" data-id="element-6254" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-6255">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" data-id="element-6256">
          <div className="flex items-center gap-3" data-id="element-6257">
            <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-lg" data-id="element-6258">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-6259" />
            </div>
            <div data-id="element-6260">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium" data-id="element-6261">
                Total Usuarios
              </p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100" data-id="element-6262">
                {users.length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" data-id="element-6263">
          <div className="flex items-center gap-3" data-id="element-6264">
            <div className="p-3 bg-green-100 dark:bg-green-800 rounded-lg" data-id="element-6265">
              <Shield className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-6266" />
            </div>
            <div data-id="element-6267">
              <p className="text-xs text-green-600 dark:text-green-400 font-medium" data-id="element-6268">
                Activos
              </p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100" data-id="element-6269">
                {users.filter(u => u.status === 'Active').length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800" data-id="element-6270">
          <div className="flex items-center gap-3" data-id="element-6271">
            <div className="p-3 bg-purple-100 dark:bg-purple-800 rounded-lg" data-id="element-6272">
              <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-6273" />
            </div>
            <div data-id="element-6274">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium" data-id="element-6275">
                Administradores
              </p>
              <p className="text-2xl font-bold text-purple-900 dark:text-purple-100" data-id="element-6276">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Users Table */}
      <Card noPadding className="overflow-hidden" data-id="element-6277">
        <div className="hidden md:block overflow-x-auto" data-id="element-6278">
          <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-6279">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-6280">
              <tr data-id="element-6281">
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-6282">
                  Usuario
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-6283">
                  Rol
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-6284">
                  Permisos
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-6285">
                  Estado
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-6286">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900" data-id="element-6287">
              {filteredUsers.map(user => {
              const {
                enabled,
                total
              } = getPermissionSummary(user.permissions);
              const pct = Math.round(enabled / total * 100);
              return <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-6288">
                    <td className="px-6 py-4" data-id="element-6289">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6290">
                        {user.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6291">
                        @{user.username} • {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6292">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' : user.role === 'delivery' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-6293">
                        {user.role === 'admin' ? 'Administrador' : user.role === 'delivery' ? 'Delivery' : 'Supervisor'}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6294">
                      <div className="flex items-center gap-3" data-id="element-6295">
                        <div className="flex-1 max-w-[120px]" data-id="element-6296">
                          <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6297">
                            <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : 'bg-orange-500'}`} style={{
                          width: `${pct}%`
                        }} data-id="element-6298" />
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums" data-id="element-6299">
                          {enabled}/{total}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6300">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-6301">
                        {user.status === 'Active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-6302">
                      <div className="flex items-center justify-end gap-2" data-id="element-6303">
                        {/* Impersonate button — only for super admin, only for non-super-admin users */}
                        {canImpersonate && user.username !== 'admin' && <button onClick={() => handleImpersonate(user)} className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 p-1.5 rounded-lg transition-colors" title={`Ingresar como ${user.name}`} data-id="element-6304">
                            <LogIn size={16} data-id="element-6305" />
                          </button>}
                        <button onClick={() => handleEdit(user)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-1" title="Editar" data-id="element-6306">
                          <Edit2 size={16} data-id="element-6307" />
                        </button>
                        {user.username !== 'admin' && <button onClick={() => handleDelete(user)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1" title="Eliminar" data-id="element-6308">
                            <Trash2 size={16} data-id="element-6309" />
                          </button>}
                      </div>
                    </td>
                  </tr>;
            })}
              {filteredUsers.length === 0 && <tr data-id="element-6310">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-6311">
                    No se encontraron usuarios
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden p-4 space-y-4" data-id="element-6312">
          {filteredUsers.map(user => {
          const {
            enabled,
            total
          } = getPermissionSummary(user.permissions);
          const pct = Math.round(enabled / total * 100);
          return <div key={user.id} className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-6313">
                <div className="flex justify-between items-start mb-3" data-id="element-6314">
                  <div className="flex items-center gap-3" data-id="element-6315">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-sm shrink-0 border border-gray-200 dark:border-gray-700" data-id="element-6316">
                      {user.name.charAt(0)}
                    </div>
                    <div data-id="element-6317">
                      <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-6318">
                        {user.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6319">
                        @{user.username}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${user.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-6320">
                    {user.status === 'Active' ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div className="space-y-2 mb-3" data-id="element-6321">
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6322">
                    {user.email}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-6323">
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' : user.role === 'delivery' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-6324">
                      {user.role === 'admin' ? 'Administrador' : user.role === 'delivery' ? 'Delivery' : 'Supervisor'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6325">
                      {enabled}/{total} permisos
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6326">
                    <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : 'bg-orange-500'}`} style={{
                  width: `${pct}%`
                }} data-id="element-6327" />
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-6328">
                  {canImpersonate && user.username !== 'admin' && <button onClick={() => handleImpersonate(user)} className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg" data-id="element-6329">
                      <LogIn size={14} data-id="element-6330" /> Ingresar como
                    </button>}
                  <button onClick={() => handleEdit(user)} className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-6331">
                    <Edit2 size={14} data-id="element-6332" /> Editar
                  </button>
                  {user.username !== 'admin' && <button onClick={() => handleDelete(user)} className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-6333">
                      <Trash2 size={14} data-id="element-6334" /> Eliminar
                    </button>}
                </div>
              </div>;
        })}
          {filteredUsers.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-6335">
              No se encontraron usuarios
            </div>}
        </div>
      </Card>

      {/* User Form Modal */}
      {showModal && <UserFormModal user={editingUser} onClose={() => {
      setShowModal(false);
      setEditingUser(null);
    }} onSave={handleSave} data-id="element-6336" />}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-6337">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-6338">
            <div className="p-6 text-center" data-id="element-6339">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6340">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-6341" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-6342">
                ¿Eliminar usuario?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6" data-id="element-6343">
                ¿Estás seguro de que deseas eliminar a{' '}
                <strong data-id="element-6344">{deleteConfirm.name}</strong>? Esta acción no se puede
                deshacer.
              </p>
              <div className="flex gap-3" data-id="element-6345">
                <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-6346">
                  Cancelar
                </Button>
                <button onClick={confirmDelete} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 transition-colors" data-id="element-6347">
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// ─── Permission Group Component ─────────────────────────────────────────────
function PermissionGroupCard({
  group,
  permissions,
  onChange,
  disabled
}: {
  group: PermissionGroup;
  permissions: Record<string, boolean>;
  onChange: (key: string, value: boolean) => void;
  disabled: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const Icon = group.icon;
  const colors = colorMap[group.color] || colorMap.gray;
  const groupKeys = group.permissions.map(p => p.key);
  const enabledCount = groupKeys.filter(k => permissions[k]).length;
  const allEnabled = enabledCount === groupKeys.length;
  const someEnabled = enabledCount > 0 && !allEnabled;
  const toggleAll = () => {
    if (disabled) return;
    const newValue = !allEnabled;
    groupKeys.forEach(k => onChange(k, newValue));
  };
  return <div className={`border rounded-xl overflow-hidden transition-all ${expanded ? colors.border : 'border-gray-200 dark:border-gray-800'}`} data-id="element-6348">
      <div className={`flex items-center gap-3 px-4 py-3 cursor-pointer select-none transition-colors ${expanded ? colors.bgLight : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`} onClick={() => setExpanded(!expanded)} data-id="element-6349">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bgLight}`} data-id="element-6350">
          <Icon size={16} className={colors.text} data-id="element-6351" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-6352">
          <div className="flex items-center gap-2" data-id="element-6353">
            <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-6354">
              {group.label}
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${allEnabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : enabledCount > 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-6355">
              {enabledCount}/{groupKeys.length}
            </span>
          </div>
        </div>
        <button type="button" onClick={e => {
        e.stopPropagation();
        toggleAll();
      }} disabled={disabled} className={`p-1 rounded transition-colors ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`} title={allEnabled ? 'Desmarcar todos' : 'Marcar todos'} data-id="element-6356">
          {allEnabled ? <CheckSquare size={18} className="text-green-600 dark:text-green-400" data-id="element-6357" /> : someEnabled ? <div className="w-[18px] h-[18px] border-2 border-amber-500 rounded flex items-center justify-center" data-id="element-6358">
              <div className="w-2 h-0.5 bg-amber-500 rounded" data-id="element-6359" />
            </div> : <Square size={18} className="text-gray-400" data-id="element-6360" />}
        </button>
        {expanded ? <ChevronDown size={16} className="text-gray-400 shrink-0" data-id="element-6361" /> : <ChevronRight size={16} className="text-gray-400 shrink-0" data-id="element-6362" />}
      </div>

      {expanded && <div className="border-t border-gray-100 dark:border-gray-800" data-id="element-6363">
          {group.permissions.map((perm, idx) => <label key={perm.key} className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'} ${idx < group.permissions.length - 1 ? 'border-b border-gray-50 dark:border-gray-800/50' : ''}`} data-id="element-6364">
              <input type="checkbox" checked={permissions[perm.key] || false} onChange={e => onChange(perm.key, e.target.checked)} disabled={disabled} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900 dark:focus:ring-white disabled:opacity-50" data-id="element-6365" />
              <div className="flex-1 min-w-0" data-id="element-6366">
                <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6367">
                  {perm.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-6368">
                  {perm.description}
                </div>
              </div>
            </label>)}
        </div>}
    </div>;
}
// ─── User Form Modal ────────────────────────────────────────────────────────
function UserFormModal({
  user,
  onClose,
  onSave
}: {
  user: AdminUser | null;
  onClose: () => void;
  onSave: (data: any) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  // Load companies for delivery assignment
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        if (isSupabaseConfigured()) {
          const data = await supabaseClient.getCompanies();
          setCompanies(data || []);
        } else {
          setCompanies(getCompanies());
        }
      } catch (e) {
        console.error('Error loading companies:', e);
        setCompanies(getCompanies());
      }
    };
    loadCompanies();
  }, []);
  const initialPerms = user ? fromDbPermissions(user.permissions) : (() => {
    const p: Record<string, boolean> = {};
    ALL_PERMISSION_KEYS.forEach(k => p[k] = false);
    return p;
  })();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    password: '',
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'supervisor',
    status: user?.status || 'Active'
  });
  const [permissions, setPermissions] = useState<Record<string, boolean>>(initialPerms);
  const [activePreset, setActivePreset] = useState<PresetId>(() => detectPreset(initialPerms));
  // Delivery company assignment
  const [assignedCompanies, setAssignedCompanies] = useState<number[]>(() => {
    return user?.permissions?.assignedCompanies || user?.permissions?.granular?.assignedCompanies || [];
  });
  const isSuperAdmin = user?.username === 'admin';
  const isDeliveryRole = formData.role === 'delivery';
  const handlePermissionChange = (key: string, value: boolean) => {
    const updated = {
      ...permissions,
      [key]: value
    };
    setPermissions(updated);
    setActivePreset(detectPreset(updated));
  };
  const applyPreset = (preset: RolePreset) => {
    if (isSuperAdmin) return;
    const newPerms = preset.getPermissions();
    setPermissions(newPerms);
    setActivePreset(preset.id);
  };
  // When role changes to delivery, set appropriate permissions
  const handleRoleChange = (newRole: string) => {
    setFormData({
      ...formData,
      role: newRole
    });
    if (newRole === 'delivery') {
      // Delivery users only need to view orders
      const deliveryPerms: Record<string, boolean> = {};
      ALL_PERMISSION_KEYS.forEach(k => deliveryPerms[k] = false);
      deliveryPerms['orders.view'] = true;
      deliveryPerms['orders.edit'] = true; // Can mark as delivered
      deliveryPerms['labels.view'] = true;
      deliveryPerms['labels.print'] = true;
      setPermissions(deliveryPerms);
      setActivePreset('custom');
    }
  };
  const toggleAssignedCompany = (companyId: number) => {
    setAssignedCompanies(prev => prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]);
  };
  const enabledCount = ALL_PERMISSION_KEYS.filter(k => permissions[k]).length;
  const pct = Math.round(enabledCount / ALL_PERMISSION_KEYS.length * 100);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dbPerms = toDbPermissions(isSuperAdmin ? (() => {
        const p: Record<string, boolean> = {};
        ALL_PERMISSION_KEYS.forEach(k => p[k] = true);
        return p;
      })() : permissions);
      // Add assigned companies for delivery users
      if (isDeliveryRole && assignedCompanies.length > 0) {
        dbPerms.assignedCompanies = assignedCompanies;
        if (dbPerms.granular) {
          dbPerms.granular.assignedCompanies = assignedCompanies;
        }
      }
      await onSave({
        ...formData,
        permissions: dbPerms
      });
    } finally {
      setSaving(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-6369">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl border border-gray-200 dark:border-gray-800 max-h-[92vh] flex flex-col" data-id="element-6370">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800 shrink-0" data-id="element-6371">
          <div data-id="element-6372">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-6373">
              {user ? 'Editar Usuario' : 'Nuevo Usuario Administrativo'}
            </h2>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1" data-id="element-6374">
              <Shield size={12} data-id="element-6375" /> La contraseña se encripta automáticamente en
              el servidor
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1" data-id="element-6376">
            <X size={20} data-id="element-6377" />
          </button>
        </div>

        {/* Scrollable Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" data-id="element-6378">
          <div className="p-6 space-y-6" data-id="element-6379">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6380">
              <div data-id="element-6381">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6382">
                  Nombre Completo
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Juan Pérez" data-id="element-6383" />
              </div>
              <div data-id="element-6384">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6385">
                  Correo Electrónico
                </label>
                <input type="email" required value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="juan@empresa.com" data-id="element-6386" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6387">
              <div data-id="element-6388">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6389">
                  Nombre de Usuario
                </label>
                <input type="text" required value={formData.username} onChange={e => setFormData({
                ...formData,
                username: e.target.value
              })} disabled={!!user} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50" placeholder="juanperez" data-id="element-6390" />
                {!!user && <p className="text-xs text-gray-400 mt-1" data-id="element-6391">
                    El username no se puede cambiar
                  </p>}
              </div>
              <div data-id="element-6392">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6393">
                  Contraseña {user && '(dejar en blanco para mantener)'}
                </label>
                <div className="relative" data-id="element-6394">
                  <input type={showPassword ? 'text' : 'password'} required={!user} value={formData.password} onChange={e => setFormData({
                  ...formData,
                  password: e.target.value
                })} className="w-full px-3 py-2.5 pr-10 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="••••••••" data-id="element-6395" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-6396">
                    {showPassword ? <EyeOff size={18} data-id="element-6397" /> : <Eye size={18} data-id="element-6398" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6399">
              <div data-id="element-6400">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6401">
                  Rol
                </label>
                <select value={formData.role} onChange={e => handleRoleChange(e.target.value)} disabled={isSuperAdmin} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50" data-id="element-6402">
                  <option value="admin" data-id="element-6403">Administrador</option>
                  <option value="supervisor" data-id="element-6404">Supervisor</option>
                  <option value="delivery" data-id="element-6405">Delivery / Repartidor</option>
                </select>
              </div>
              <div data-id="element-6406">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-6407">
                  Estado
                </label>
                <select value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value
              })} disabled={isSuperAdmin} className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50" data-id="element-6408">
                  <option value="Active" data-id="element-6409">Activo</option>
                  <option value="Inactive" data-id="element-6410">Inactivo</option>
                </select>
              </div>
            </div>

            {/* ─── Delivery Company Assignment ─── */}
            {isDeliveryRole && <div className="border border-blue-200 dark:border-blue-800 rounded-xl overflow-hidden" data-id="element-6411">
                <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 border-b border-blue-200 dark:border-blue-800" data-id="element-6412">
                  <h3 className="text-sm font-bold text-blue-900 dark:text-blue-100 flex items-center gap-2" data-id="element-6413">
                    <Building2 size={16} data-id="element-6414" /> Empresas y Sucursales Asignadas
                  </h3>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5" data-id="element-6415">
                    El repartidor solo verá los pedidos de las empresas
                    seleccionadas
                  </p>
                </div>
                <div className="p-4 max-h-64 overflow-y-auto" data-id="element-6416">
                  {companies.length === 0 ? <p className="text-sm text-gray-500 text-center py-4" data-id="element-6417">
                      Cargando empresas...
                    </p> : <div className="space-y-2" data-id="element-6418">
                      {companies.map((company: any) => {
                  const isSelected = assignedCompanies.includes(company.id);
                  return <label key={company.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-6419">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleAssignedCompany(company.id)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-6420" />
                            <div className="flex-1 min-w-0" data-id="element-6421">
                              <div className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-6422">
                                {company.name}
                              </div>
                              {company.branches && company.branches.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-6423">
                                    {company.branches.map((branch: any) => <span key={branch.id} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded" data-id="element-6424">
                                        {branch.name}
                                      </span>)}
                                  </div>}
                            </div>
                          </label>;
                })}
                    </div>}
                </div>
                {assignedCompanies.length > 0 && <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800" data-id="element-6425">
                    <p className="text-xs text-blue-700 dark:text-blue-300 font-medium" data-id="element-6426">
                      {assignedCompanies.length} empresa(s) asignada(s)
                    </p>
                  </div>}
              </div>}

            {/* ─── Permissions Section ─── */}
            <div className="border-t border-gray-200 dark:border-gray-800 pt-6" data-id="element-6427">
              <div className="flex items-center justify-between mb-4" data-id="element-6428">
                <div data-id="element-6429">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-6430">
                    <Shield size={18} data-id="element-6431" /> Permisos de Acceso
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-6432">
                    Configura exactamente qué puede hacer este usuario
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-6433">
                  <div className="h-2 w-24 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-6434">
                    <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-blue-500' : 'bg-orange-500'}`} style={{
                    width: `${pct}%`
                  }} data-id="element-6435" />
                  </div>
                  <span className="text-xs font-mono text-gray-500 tabular-nums" data-id="element-6436">
                    {enabledCount}/{ALL_PERMISSION_KEYS.length}
                  </span>
                </div>
              </div>

              {/* Role Presets */}
              {!isSuperAdmin && !isDeliveryRole && <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5" data-id="element-6437">
                  {ROLE_PRESETS.map(preset => {
                const PresetIcon = preset.icon;
                const isActive = activePreset === preset.id;
                return <button key={preset.id} type="button" onClick={() => applyPreset(preset)} className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 text-center transition-all ${isActive ? 'border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'}`} data-id="element-6438">
                        <PresetIcon size={18} data-id="element-6439" />
                        <span className="text-xs font-bold" data-id="element-6440">
                          {preset.label}
                        </span>
                      </button>;
              })}
                </div>}

              {isSuperAdmin && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-5" data-id="element-6441">
                  <p className="text-sm text-green-800 dark:text-green-300 font-medium flex items-center gap-2" data-id="element-6442">
                    <Shield size={16} data-id="element-6443" /> El administrador principal siempre
                    tiene acceso total.
                  </p>
                </div>}

              {isDeliveryRole && <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-5" data-id="element-6444">
                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium flex items-center gap-2" data-id="element-6445">
                    <Package size={16} data-id="element-6446" /> Los repartidores tienen permisos
                    limitados a órdenes y etiquetas. Puedes personalizar abajo.
                  </p>
                </div>}

              {/* Permission Groups */}
              <div className="space-y-2" data-id="element-6447">
                {PERMISSION_GROUPS.map(group => <PermissionGroupCard key={group.id} group={group} permissions={isSuperAdmin ? (() => {
                const p: Record<string, boolean> = {};
                ALL_PERMISSION_KEYS.forEach(k => p[k] = true);
                return p;
              })() : permissions} onChange={handlePermissionChange} disabled={isSuperAdmin} data-id="element-6448" />)}
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 shrink-0 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl" data-id="element-6449">
          <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-6450">
            Cancelar
          </Button>
          <Button type="submit" fullWidth disabled={saving} onClick={handleSubmit} data-id="element-6451">
            {saving ? 'Guardando...' : user ? 'Actualizar Usuario' : 'Crear Usuario'}
          </Button>
        </div>
      </div>
    </div>;
}